'use client';

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Send, User, Phone, Mail, Users, MessageSquare, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { ModalConfig, ListaFreeFormData, Reservation } from '../types/events';

interface ListaFreeModalProps {
  config: ModalConfig;
  onClose: () => void;
}

const GENERO_OPTIONS = ['Femenino', 'Masculino', 'Prefiero no decir'];
const CANTIDAD_OPTIONS = ['1 persona', '2 personas', '3 personas', '4 personas', '5+ personas'];

// Backend integration point: replace localStorage with POST /api/reservations
function saveReservation(res: Reservation): void {
  const existing: Reservation[] = JSON.parse(localStorage.getItem('olivera_reservations') || '[]');
  existing.push(res);
  localStorage.setItem('olivera_reservations', JSON.stringify(existing));
}

export default function ListaFreeModal({ config, onClose }: ListaFreeModalProps) {
  const { event } = config;
  const isAdnWomenOnly = event.venue === 'adn';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ListaFreeFormData>({
    defaultValues: {
      nombre: '',
      telefono: '',
      email: '',
      genero: isAdnWomenOnly ? 'Femenino' : '',
      instagram: '',
      cantidad: '1 persona',
      comentarios: '',
    },
  });

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const onSubmit = async (data: ListaFreeFormData) => {
    await new Promise((r) => setTimeout(r, 800));

    if (isAdnWomenOnly && data.genero !== 'Femenino') {
      toast.error('La Lista Free de ADN es exclusiva para mujeres.');
      return;
    }

    const reservation: Reservation = {
      id: `res-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
      nombre: data.nombre,
      telefono: data.telefono,
      email: data.email || '',
      genero: data.genero || 'No especificado',
      instagram: data.instagram || '',
      cantidad: data.cantidad || '1 persona',
      comentarios: data.comentarios || '',
      eventId: event.id,
      eventTitle: event.title,
      venueName: event.venueName,
      dateLabel: event.dateLabel,
      venue: event.venue,
      timestamp: new Date().toISOString(),
      status: 'confirmado',
    };

    saveReservation(reservation);

    toast.success(`¡Listo! Estás en la lista para ${event.title} (${event.dateLabel}) 🎉`);
    reset();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl border border-border animate-slide-up max-h-[90vh] overflow-y-auto scrollbar-hide">
        {/* Modal Header */}
        <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-extrabold text-base text-foreground">📋 Lista Free</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {event.title} · {event.dayLabel} {event.dateLabel}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Women only notice */}
        {isAdnWomenOnly && (
          <div className="mx-5 mt-4 badge-women-only rounded-xl px-4 py-3 flex items-start gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold">Lista Free exclusiva para mujeres</p>
              <p className="text-xs font-medium opacity-80 mt-0.5">
                Solo se aceptan inscripciones de mujeres para ADN Spring Day.
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="px-5 py-4 flex flex-col gap-4">
          {/* Nombre */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <User size={12} />
              Nombre y Apellido *
            </label>
            <input
              type="text"
              placeholder="Ej: Valentina García"
              className="bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('nombre', { required: 'El nombre es obligatorio', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })}
            />
            {errors.nombre && (
              <p className="text-xs text-red-400 flex items-center gap-1">
                <AlertCircle size={11} /> {errors.nombre.message}
              </p>
            )}
          </div>

          {/* Teléfono */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Phone size={12} />
              Teléfono / WhatsApp *
            </label>
            <input
              type="tel"
              placeholder="Ej: 2236123456"
              className="bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('telefono', {
                required: 'El teléfono es obligatorio',
                pattern: { value: /^[0-9+\s\-()]{7,15}$/, message: 'Teléfono inválido' },
              })}
            />
            {errors.telefono && (
              <p className="text-xs text-red-400 flex items-center gap-1">
                <AlertCircle size={11} /> {errors.telefono.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Mail size={12} />
              Email
              <span className="text-muted-foreground font-normal">(opcional)</span>
            </label>
            <input
              type="email"
              placeholder="Ej: valen@gmail.com"
              className="bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('email', {
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
              })}
            />
            {errors.email && (
              <p className="text-xs text-red-400 flex items-center gap-1">
                <AlertCircle size={11} /> {errors.email.message}
              </p>
            )}
          </div>

          {/* Género */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Users size={12} />
              Género *
              {isAdnWomenOnly && <span className="badge-women-only text-[10px] px-2 py-0.5 rounded-full">Solo mujeres</span>}
            </label>
            <select
              className="bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('genero', { required: 'Seleccioná tu género' })}
              disabled={isAdnWomenOnly}
            >
              <option value="">Seleccionar...</option>
              {GENERO_OPTIONS.map((g) => (
                <option key={`genero-${g}`} value={g}>{g}</option>
              ))}
            </select>
            {errors.genero && (
              <p className="text-xs text-red-400 flex items-center gap-1">
                <AlertCircle size={11} /> {errors.genero.message}
              </p>
            )}
          </div>

          {/* Instagram */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <span className="text-xs">IG</span>
              Instagram
              <span className="text-muted-foreground font-normal">(opcional)</span>
            </label>
            <input
              type="text"
              placeholder="@tuusuario"
              className="bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('instagram')}
            />
          </div>

          {/* Cantidad */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Users size={12} />
              Cantidad de personas
            </label>
            <select
              className="bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('cantidad')}
            >
              {CANTIDAD_OPTIONS.map((c) => (
                <option key={`cant-${c}`} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Comentarios */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <MessageSquare size={12} />
              Comentarios
              <span className="text-muted-foreground font-normal">(opcional)</span>
            </label>
            <textarea
              rows={2}
              placeholder="¿Alguna consulta o aclaración?"
              className="bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all resize-none"
              {...register('comentarios')}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm text-white transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed mt-2"
            style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}
          >
            {isSubmitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send size={15} />
                Confirmar Lista Free
              </>
            )}
          </button>

          <p className="text-center text-xs text-muted-foreground">
            Tus datos son confidenciales y solo se usan para gestionar tu lugar.
          </p>
        </form>
      </div>
    </div>
  );
}